import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Phone,
  Mail,
  MapPin,
  User,
  Users,
  Calendar,
  ArrowRight
} from 'lucide-react';
import WhatsAppIcon from './WhatsAppIcon';
import HomamSanctumDarshan from './HomamSanctumDarshan';
import { MOCK_NAKSHATRAS } from '../data/homamData';
import { getHomamPath } from '../utils/domain';

export function HomamEnquiryForm({
  initialNakshatraId = 1,
  initialPreferredDay = 'Dedicated Star Day',
  source = 'Website Form',
  showDarshanImage = false
}) {
  const navigate = useNavigate();
  const [sameAsMobile, setSameAsMobile] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    whatsappNumber: '',
    email: '',
    nakshatraId: initialNakshatraId,
    preferredDay: initialPreferredDay,
    participantsCount: 1,
    city: '',
    gothram: '',
    message: ''
  });

  useEffect(() => {
    if (initialNakshatraId) {
      setFormData((p) => ({ ...p, nakshatraId: initialNakshatraId }));
    }
  }, [initialNakshatraId]);

  const selectedNakshatra =
    MOCK_NAKSHATRAS.find((n) => n.id === Number(formData.nakshatraId)) || MOCK_NAKSHATRAS[0];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const up = { ...prev, [name]: value };
      if (name === 'mobile') {
        up.whatsappNumber = value;
      }
      return up;
    });
  };

  // Helper to build booking payload and proceed directly to Seva Selection
  const proceedToBookingPage = (customUser = null) => {
    let activeUser = customUser;
    if (!activeUser) {
      try {
        const stored = localStorage.getItem('user');
        if (stored) activeUser = JSON.parse(stored);
      } catch (e) {
        activeUser = null;
      }
    }

    const devoteePayload = {
      name: formData.name.trim(),
      mobile: formData.mobile.trim(),
      whatsappNumber: (sameAsMobile ? formData.mobile : formData.whatsappNumber || formData.mobile).trim(),
      email: formData.email ? formData.email.trim() : (activeUser?.email || ''),
      userId: activeUser?._id || null,
      nakshatraId: Number(formData.nakshatraId),
      nakshatraNameEn: selectedNakshatra.nameEn,
      nakshatraNameTa: selectedNakshatra.nameTa,
      homamDate: selectedNakshatra.assignedDate,
      preferredDay: `Day ${selectedNakshatra.orderIndex} • ${selectedNakshatra.assignedDate}`,
      participantsCount: Number(formData.participantsCount) || 1,
      city: formData.city.trim(),
      gothram: formData.gothram.trim(),
      message: formData.message.trim(),
      source: source,
      bookingId: `SGSMH${Date.now().toString().slice(-6)}`,
      _id: 'pending_checkout',
    };

    // Navigate to the separate dedicated booking page to choose plan, pay, and enter UTR!
    navigate(getHomamPath('/booking'), {
      state: {
        booking: devoteePayload,
        nakshatra: selectedNakshatra,
      },
    });
  };

  // Submit Form - Direct seamless registration without login block
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!formData.name.trim() || !formData.mobile.trim() || !formData.email.trim() || !formData.city.trim()) {
      setErrorMessage('Please fill in all required fields (Name, Mobile, Email, City).');
      return;
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    if (formData.mobile.trim().length !== 10) {
      setErrorMessage('Please enter a valid 10-digit mobile number.');
      return;
    }

    // Proceed straight to Seva Plan Selection & Booking
    proceedToBookingPage();
  };

  const formElement = (
    <div className="homam-form-frame">
      <div style={{ textAlign: 'left', marginBottom: 20 }}>
        <div className="homam-emblem-badge" style={{ marginBottom: 10 }}>
          <Sparkles className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
          <span>Homam Registration</span>
        </div>
        <h3 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f2d5c', margin: '0 0 6px 0' }}>
          Join the 27 Nakshatra Maha Homam
        </h3>
        <p style={{ fontSize: 13, color: '#475569', margin: 0 }}>
          Fill the sacred registration form below. Sri Gnana Sai Baba Mandir seva team will connect with you with personalized Homam details.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="homam-form-2col">
          <div className="homam-field">
            <label><User className="w-3.5 h-3.5" style={{ color: '#b8860b' }} /> Devotee Full Name *</label>
            <input
              type="text"
              name="name"
              placeholder="e.g. Ramesh Kumar / Smt. Lakshmi"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="homam-field">
            <label><Mail className="w-3.5 h-3.5" style={{ color: '#b8860b' }} /> Devotee Email Address *</label>
            <input
              type="email"
              name="email"
              placeholder="e.g. devotee@gmail.com"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="homam-field">
            <label><Phone className="w-3.5 h-3.5" style={{ color: '#b8860b' }} /> Mobile Number *</label>
            <input
              type="tel"
              name="mobile"
              maxLength={10}
              placeholder="10-digit mobile number"
              value={formData.mobile}
              onChange={handleChange}
              required
            />
          </div>

          <div className="homam-field">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label><WhatsAppIcon size={14} style={{ color: '#25D366', marginRight: 4 }} /> WhatsApp Number *</label>
              <label style={{ fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="checkbox"
                  checked={sameAsMobile}
                  onChange={(e) => {
                    setSameAsMobile(e.target.checked);
                    if (e.target.checked) {
                      setFormData((prev) => ({ ...prev, whatsappNumber: prev.mobile }));
                    }
                  }}
                  style={{ width: 'auto' }}
                />
                <span>Same</span>
              </label>
            </div>
            <input
              type="tel"
              name="whatsappNumber"
              maxLength={10}
              placeholder="10-digit WhatsApp"
              value={sameAsMobile ? formData.mobile : formData.whatsappNumber}
              onChange={handleChange}
              disabled={sameAsMobile}
              required
            />
          </div>

          <div className="homam-field">
            <label><Sparkles className="w-3.5 h-3.5" style={{ color: '#b8860b' }} /> Select Your Birth Star (Nakshatra) *</label>
            <select
              name="nakshatraId"
              value={formData.nakshatraId}
              onChange={handleChange}
              required
            >
              {MOCK_NAKSHATRAS.map((star) => (
                <option key={star.id} value={star.id}>
                  Day {star.orderIndex}: {star.nameEn} ({star.nameTa}) — {star.assignedDate}
                </option>
              ))}
            </select>
          </div>

          <div className="homam-field">
            <label><Calendar className="w-3.5 h-3.5" style={{ color: '#b8860b' }} /> Homam Date (Auto-Calculated) *</label>
            <input
              type="text"
              name="preferredDay"
              value={`Day ${selectedNakshatra.orderIndex} - ${selectedNakshatra.assignedDate} (${selectedNakshatra.nameEn})`}
              readOnly
              style={{ background: '#FAF7F2', fontWeight: 600, color: '#0f2d5c' }}
            />
          </div>

          <div className="homam-field">
            <label><Users className="w-3.5 h-3.5" style={{ color: '#b8860b' }} /> Number of Family Participants *</label>
            <select
              name="participantsCount"
              value={formData.participantsCount}
              onChange={handleChange}
            >
              {[1, 2, 3, 4, 5, 6].map((num) => (
                <option key={num} value={num}>{num} {num === 1 ? 'Devotee (Individual)' : `Family Members (Up to ${num})`}</option>
              ))}
            </select>
          </div>

          <div className="homam-field">
            <label><MapPin className="w-3.5 h-3.5" style={{ color: '#b8860b' }} /> Your City / Town *</label>
            <input
              type="text"
              name="city"
              placeholder="e.g. Chennai / Coimbatore / Bengaluru"
              value={formData.city}
              onChange={handleChange}
              required
            />
          </div>
        </div>



        {errorMessage && (
          <div style={{ marginTop: 14, padding: 12, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 12, fontSize: 13 }}>
            ⚠️ {errorMessage}
          </div>
        )}

        <div style={{ marginTop: 20 }}>
          <button
            type="submit"
            className="btn-homam-submit"
          >
            <span>Submit Homam Registration</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <p style={{ textAlign: 'center', fontSize: 11.5, color: '#64748b', marginTop: 10 }}>
          🔒 Your details are handled with pure sanctity by the Mandir Seva Trust.
        </p>
      </form>
    </div>
  );

  if (!showDarshanImage) {
    return formElement;
  }

  return (
    <div className="homam-enquiry-section-grid">
      {formElement}
      <HomamSanctumDarshan />
    </div>
  );
}

export default HomamEnquiryForm;
